import { useTranslation, useTranslate } from 'module/hooks/use-translation';
import { NodeViewContent } from '@tiptap/react';
import {
  ParticipantGroupOption,
  CustomSectionState,
  QuestionCustomSectionAttributes,
  CustomSectionType,
} from 'api/custom-section/types';
import { useState, useCallback } from 'react';
import { PromptType } from 'components/organisms/document-editor/extensions/custom-section/types';
import { getConfigurationPlaceholderNodes } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-content-utils';
import {
  SectionPreview,
  SectionTitle,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { SectionWrapper } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { MeetingTemplatePagePreview } from 'components/organisms/meeting-preview/meeting-page-template-preview';
import { PromptInput } from './prompt-input';
import { defaultParticipantsFilter } from '../../../../types/ui-types';
import { defaultCustomSectionNodeConfig } from '../../../../utils';
import { QuestionSectionComponentProps } from '../types';

const constructPrompt = (attributes: QuestionCustomSectionAttributes) => {
  const payload = {
    ...defaultCustomSectionNodeConfig[CustomSectionType.Question].config,
    ...attributes.config,
  };

  return {
    title: attributes.title,
    question: payload.question,
    aiContextChecked: payload.aiContext ? true : false,
    aiContext: payload.aiContext,
    groupBy: payload.groupBy,
    generateAs: payload.generateAs,
    participants: payload.participants,
    participantsFilter:
      payload.participants === ParticipantGroupOption.Custom
        ? (payload.participantsFilter ?? defaultParticipantsFilter)
        : defaultParticipantsFilter,
  };
};

export const QuestionPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  allowResolve,
  contentEditable,
  updateContent,
  onOpen,
  pageType,
  pageTriggerListFilter,
}: QuestionSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;
  const [prompt, setPrompt] = useState<PromptType>(constructPrompt(node.attrs));
  const [isGenerating, setIsGenerating] = useState(false);

  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );
  const translate = useTranslate();
  const [showPreview, setShowPreview] = useState(false);

  const getNodeAsPayload = useCallback(() => {
    const payload = node.toJSON();

    if (!('content' in payload)) {
      payload.content = [];
    }

    return payload;
  }, [node]);

  const onGenerate = (userPrompt: PromptType) => {
    setIsGenerating(true);
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      config: {
        question: userPrompt.question,
        aiContext: userPrompt.aiContext,
        groupBy: userPrompt.groupBy,
        generateAs: userPrompt.generateAs,
        participants: userPrompt.participants,
        participantsFilter:
          userPrompt.participants === ParticipantGroupOption.Custom
            ? userPrompt.participantsFilter
            : undefined,
      },
      state: CustomSectionState.Closed,
    };

    onDone(attributes).then(() => {
      updateContent?.(
        getConfigurationPlaceholderNodes(
          {
            groupBy: userPrompt.groupBy,
            generateAs: userPrompt.generateAs,
          },
          translate,
        ),
        attributes,
      );
      setIsGenerating(false);
    });
  };

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(constructPrompt(node.attrs));
    onUndo();
  }, [onUndo, node.attrs, node.content?.size, onRemove]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          isGenerating={isGenerating}
          onCancel={onRemove}
          onUndo={onClose}
          allowResolve={allowResolve}
          getNodeAsPayload={getNodeAsPayload}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      ) : (
        <SectionPreview
          onClick={() => {
            onOpen();
          }}
        >
          <SectionWrapper>
            <SectionTitle>{prompt.title || defaultTitleLabel}</SectionTitle>
            <NodeViewContent />
          </SectionWrapper>
        </SectionPreview>
      )}
      {showPreview && (
        <MeetingTemplatePagePreview
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          getPayload={getNodeAsPayload}
        />
      )}
    </div>
  );
};
